import * as React from 'react';
import type {AppProps} from 'next/app';
import {CacheProvider, EmotionCache} from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import createEmotionCache from '../utility/createEmotionCache';
import '../theming/globals.css';
import '../theming/fonts.css';
import {SosThemeProvider} from "../theming/ThemeContext";
import {
    ApolloClient,
    ApolloProvider,
    DefaultOptions,
    HttpLink,
    InMemoryCache
} from "@apollo/client";
import possibleTypes from '../../packages/graphql-layer/src/__downloaded__/possibleTypes.json';
import {CartProvider} from "../context/CartContext";
import {SessionProvider} from "../context/SessionContext";
import Usercentrics from "../extenions/usercentrics/Usercentrics";
import {useScrollToAnchor} from "../utility/useScrollToAnchor";
import { TrackingProvider } from 'tracking/context/TrackingContext';

interface MyAppProps extends AppProps {
    emotionCache?: EmotionCache;
    e2eTestMode?: boolean;
}

const clientSideEmotionCache = createEmotionCache();
const defaultOptions: DefaultOptions = {
    watchQuery: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'ignore',
    },
    query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
    },
}

const client = new ApolloClient({
                                    link: new HttpLink({
                                                           uri: '/api/raeQl'
                                                       }),
                                    cache: new InMemoryCache({
                                                                 possibleTypes: possibleTypes,
                                                                 typePolicies: {
                                                                     Query: {
                                                                         fields: {
                                                                             content: {
                                                                                 merge(_existing,
                                                                                       incoming) {
                                                                                     return incoming;
                                                                                 }
                                                                             },
                                                                         },
                                                                     },
                                                                 }
                                                             }),
                                    defaultOptions: defaultOptions
                                });


const MyApp: React.FunctionComponent<MyAppProps> = (props) => {
    const {
        Component,
        emotionCache = clientSideEmotionCache,
        pageProps,
        e2eTestMode
    } = props;
    useScrollToAnchor();

    React.useEffect(() => {
        const handleChunkError = (event: PromiseRejectionEvent) => {
            const error = event.reason;
            const isChunkError =
                error?.name === 'ChunkLoadError' ||
                error?.message?.includes('Loading chunk') ||
                error?.message?.includes('Failed to fetch dynamically imported module');
            if (!isChunkError) return;

            // Guard against infinite reload loop
            const key = 'chunkErrorReloadAt';
            const lastReload = sessionStorage.getItem(key);
            const now = Date.now();
            if (!lastReload || now - parseInt(lastReload) > 10_000) {
                sessionStorage.setItem(key, String(now));
                window.location.reload();
            }
        };
        window.addEventListener('unhandledrejection', handleChunkError);
        return () => window.removeEventListener('unhandledrejection', handleChunkError);
    }, []);

    return (
        <CacheProvider value={emotionCache}>
            <SosThemeProvider>
                <SessionProvider>
                    <CartProvider>
                        <ApolloProvider client={client}>
                            <TrackingProvider>
                                <CssBaseline/>
                                <Component {...pageProps} />
                                {!e2eTestMode && <Usercentrics pageProps={pageProps}/>}
                            </TrackingProvider>
                        </ApolloProvider>
                    </CartProvider>
                </SessionProvider>
            </SosThemeProvider>

        </CacheProvider>
    );
};

export default MyApp;